import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Clock, LogOut } from "lucide-react";

/**
 * Full-screen gate shown to a user whose KYC has been submitted
 * (`profile_completed=true`) but an admin has not yet verified them
 * (`profile_verified=false`). Admins/approvers are exempt so they can keep
 * working and process the queue.
 *
 * Pass `children` — they are rendered ONLY when the user is clear to pass.
 */
export default function PendingVerificationGate({ children }) {
  const { user, hasPermission, logout } = useAuth();

  const isApprover = hasPermission("pending_approvals");
  const blocked = !!user
    && user.profile_completed === true
    && user.profile_verified === false
    && !isApprover;

  if (!blocked) return children;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="pending-verification-gate">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <Clock className="w-7 h-7 text-amber-500" />
        </div>
        <span className="label-overline text-zinc-500">Documents under review</span>
        <h2
          className="font-display text-3xl font-black tracking-tighter mt-2 mb-3"
          data-testid="pending-verification-title"
        >
          Thanks, {user.name?.split(" ")[0] || "there"}.
        </h2>
        <p className="text-sm text-zinc-600 leading-relaxed" data-testid="pending-verification-message">
          We've received your documents. An administrator is reviewing them now — you'll
          get a notification the moment they're verified, and the full app will unlock automatically.
        </p>
        <div className="mt-6 flex flex-col gap-2 items-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Submitted &middot; awaiting admin verification
          </div>
          <Button
            variant="ghost"
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="text-zinc-500 mt-2"
            data-testid="pending-verification-logout"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
